'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Linkedin, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/encuentro/ui/field'
import {
  RECOVERY_LINK_FAILED_CODE,
  MAGIC_LINK_FAILED_CODE,
  loginErrorMessageKey,
} from '@/lib/utils/auth-urls'

import { AuthHeading, AuthShell, authLinkClass } from './auth-shell'
import { ConsentLine } from './consent-line'
import { PasswordField } from './password-field'
import {
  isRedirectError,
  startLinkedInSignIn,
  type FormAuthAction,
  type LinkedInAuthAction,
} from './types'
import { LoginCodePanel } from './login-code-panel'
import type { RequestLoginCodeAction, VerifyLoginCodeAction } from '@/lib/auth/login-code'
import {
  forgetRememberedEmail,
  readRememberedEmail,
  rememberEmail,
} from '@/lib/auth/remembered-email'

export interface LoginScreenProps {
  /** The school of an org-branded login (`?org=`, or the last one remembered). */
  organization: { name: string; logo_url: string | null; slug: string } | null
  referralCode?: string
  referrerName?: string
  initialFlow?: 'signup'
  /** The address of a sign-up waiting for its confirmation (ALU-280), to prefill sign in. */
  pendingEmail?: string
  /** The classic server actions, unchanged (app/login/actions.ts). */
  actions: { login: FormAuthAction; signup: FormAuthAction; signInWithLinkedIn: LinkedInAuthAction }
  /**
   * "Entrar con un código por mail" (app/login/code-actions.ts). Without it the
   * entry is not offered, which keeps the screen as it was.
   */
  codeActions?: { request: RequestLoginCodeAction; verify: VerifyLoginCodeAction }
  /** The language switcher. */
  locale?: ReactNode
}

/**
 * Sign in and sign up (ALU-73). The behaviour is the classic `LoginForm`'s,
 * line for line: the mode comes from `?flow=signup` with a school, the
 * invitation locks the email, "account already exists" switches to sign in
 * and keeps a persistent alert, `?errorCode=` explains a dead recovery or
 * magic link, and every submit goes to the same server action. Only the
 * presentation and the copy (voseo, no tagline) are new.
 */
// `?error=` code → its message under encuentro.auth (the classic `auth` namespace is
// off limits here: scripts/check-encuentro.sh).
const LOGIN_ERROR_TEXT_KEY = {
  'errors.authenticationFailedRetry': 'login.error.authenticationFailed',
  'errors.sessionExpired': 'login.error.sessionExpired',
  'errors.sessionExpiredPrivacy': 'login.error.sessionExpiredPrivacy',
} as const

export function LoginScreen({
  organization,
  referralCode,
  referrerName,
  initialFlow,
  pendingEmail,
  actions,
  codeActions,
  locale,
}: LoginScreenProps) {
  const t = useTranslations('encuentro.auth')
  const [isLoading, setIsLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const searchParams = useSearchParams()

  // Only a known `?error=` code is shown; anything else is ignored (ALU-518).
  const errorKey = loginErrorMessageKey(searchParams.get('error'))
  const errorCode = searchParams.get('errorCode')
  const isRecoveryLinkFailed = errorCode === RECOVERY_LINK_FAILED_CODE
  const isMagicLinkFailed = errorCode === MAGIC_LINK_FAILED_CODE
  const errorMessage = isRecoveryLinkFailed
    ? t('login.error.recoveryLinkFailed')
    : isMagicLinkFailed
      ? t('login.error.magicLinkFailed')
      : errorKey
        ? t(LOGIN_ERROR_TEXT_KEY[errorKey])
        : null
  const initialEmail = searchParams.get('email') || ''
  const flow = searchParams.get('flow')
  const nextUrl = searchParams.get('next')

  const derivedMode = useMemo((): 'login' | 'signup' => {
    return (initialFlow === 'signup' || flow === 'signup') && organization ? 'signup' : 'login'
  }, [initialFlow, flow, organization])

  const [userOverride, setUserOverride] = useState<'login' | 'signup' | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing with URL params is valid
    setUserOverride(null)
  }, [derivedMode])
  const mode = userOverride ?? derivedMode

  const [accountFoundEmail, setAccountFoundEmail] = useState<string | null>(null)
  const [passwordKey, setPasswordKey] = useState(0)
  const [codeMode, setCodeMode] = useState(false)

  // The address of the last sign-in on this device (session audit R5). Read
  // after mount: localStorage does not exist on the server render.
  const [remembered, setRemembered] = useState<string | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reading browser storage after mount
    setRemembered(readRememberedEmail())
  }, [])

  const setMode = (next: 'login' | 'signup' | null) => {
    setUserOverride(next)
    setAccountFoundEmail(null)
  }

  const isInvitationFlow = Boolean(initialEmail && initialFlow === 'signup' && organization)
  const emailLocked = isInvitationFlow && mode === 'signup'

  useEffect(() => {
    if (errorMessage) toast.error(t('login.toast.loginError'), { description: errorMessage })
  }, [errorMessage, t])

  const handleLinkedInLogin = async () => {
    setIsLinkedInLoading(true)
    try {
      // No invitation token here, as in the classic form: the invitation
      // cookie set by /join and the email match in the callback handle it.
      // In the native app the provider opens in the in-app browser (ALU-519/619).
      const result = await startLinkedInSignIn(
        actions.signInWithLinkedIn,
        undefined,
        mode,
        organization?.slug,
        nextUrl
      )
      if (result && 'opened' in result) {
        if (result.opened) setIsLinkedInLoading(false)
        return
      }
      if (result?.error) {
        toast.error(t('login.toast.linkedinFailed'), { description: result.error })
        setIsLinkedInLoading(false)
      } else if (result === undefined) {
        setTimeout(() => {
          setIsLinkedInLoading(false)
          toast.error(t('login.toast.linkedinNoResponse'), {
            description: t('login.toast.linkedinNoResponseBody'),
          })
        }, 5000)
      }
    } catch (err) {
      if (isRedirectError(err)) return
      toast.error(t('login.toast.linkedinFailed'), {
        description: t('login.toast.linkedinConnectError'),
      })
      setIsLinkedInLoading(false)
    }
  }

  const handleSubmit = async (formData: FormData) => {
    setIsLoading(true)
    try {
      const serverAction = mode === 'login' ? actions.login : actions.signup
      const result = await serverAction(formData)

      if (result?.error) {
        if (result.code === 'already_registered' || result.code === 'user_already_exists') {
          const submittedEmail = (formData.get('email')?.toString().trim() ?? '').toLowerCase()
          setAccountFoundEmail(submittedEmail || initialEmail || null)
          setPasswordKey((k) => k + 1)
          setUserOverride('login')
          setIsLoading(false)
          return
        }
        toast.error(t('login.toast.authFailed'), { description: result.error })
        setIsLoading(false)
      }
      // No error means the action redirected (signed in, onboarding, or "Revisá
      // tu mail" when the email must be confirmed first): keep the spinner.
    } catch (e) {
      if (isRedirectError(e)) {
        // Signed in (or on its way): this address is the one to offer next time.
        if (mode === 'login') rememberEmail(formData.get('email')?.toString() ?? '')
        return
      }
      console.error('Login error:', e)
      toast.error(t('login.toast.somethingWrong'), { description: t('login.toast.tryLater') })
      setIsLoading(false)
    }
  }

  const busy = isLoading || isLinkedInLoading
  const title =
    mode === 'signup' && organization
      ? t('login.signupTitle', { orgName: organization.name })
      : t('login.title')
  const subtitle =
    mode === 'signup'
      ? isInvitationFlow
        ? t('login.signupSubtitleInvited')
        : t('login.signupSubtitle')
      : t('login.subtitle')
  const forgotHref = (email: string) => `/forgot-password?email=${encodeURIComponent(email)}`

  return (
    <AuthShell
      org={organization ? { name: organization.name, logoUrl: organization.logo_url } : null}
      locale={locale}
    >
      <div data-testid="encuentro-login" data-mode={mode} className="grid gap-6">
        {/* In code mode the panel carries the one heading ("Entrá con un código"). */}
        {!(codeMode && codeActions) && <AuthHeading title={title} subtitle={subtitle} />}

        {referralCode && organization && (
          <Alert
            look="encuentro"
            tone="positive"
            title={
              referrerName
                ? t('login.referral.byName', { name: referrerName })
                : t('login.referral.byMember')
            }
            body={t('login.referral.body')}
          />
        )}

        {isInvitationFlow && organization && mode === 'signup' && !accountFoundEmail && (
          <Alert
            look="encuentro"
            tone="info"
            title={t('login.invitation.title')}
            body={t('login.invitation.body')}
          />
        )}

        {accountFoundEmail && (
          <Alert
            look="encuentro"
            tone="warning"
            title={t('login.accountFound.title', { email: accountFoundEmail })}
            body={
              <div className="grid gap-2">
                <p>
                  {organization
                    ? t('login.accountFound.body', { orgName: organization.name })
                    : t('login.accountFound.bodyGeneric')}
                </p>
                <Link href={forgotHref(accountFoundEmail)} className={authLinkClass}>
                  {t('login.accountFound.forgotPassword')}
                </Link>
              </div>
            }
          />
        )}

        {errorMessage && (
          <Alert
            look="encuentro"
            tone="danger"
            title={t('login.error.title')}
            body={
              <div className="grid gap-2">
                <p>{errorMessage}</p>
                {isRecoveryLinkFailed ? (
                  <Link
                    href={`/forgot-password${initialEmail ? `?email=${encodeURIComponent(initialEmail)}` : ''}`}
                    className={authLinkClass}
                  >
                    {t('login.error.newLink')}
                  </Link>
                ) : isMagicLinkFailed ? (
                  <Link href="/start" className={authLinkClass}>
                    {t('login.error.newLink')}
                  </Link>
                ) : (
                  <p className="text-en-ink-muted">{t('login.error.tryPassword')}</p>
                )}
              </div>
            }
          />
        )}

        {organization && !(codeMode && codeActions) && (
          <div className="grid gap-4">
            <Button
              look="encuentro"
              variant="secondary"
              block
              onClick={handleLinkedInLogin}
              disabled={busy}
            >
              {isLinkedInLoading ? (
                <Loader2 aria-hidden className="animate-spin" />
              ) : (
                <Linkedin aria-hidden />
              )}
              {isLinkedInLoading
                ? t('login.linkedinConnecting')
                : mode === 'signup'
                  ? t('login.linkedinSignUp')
                  : t('login.linkedinSignIn')}
            </Button>
            {emailLocked && (
              <p className="text-center text-en-meta text-en-ink-muted">
                {t('login.invitedSameEmail')}
              </p>
            )}
            <div className="flex items-center gap-3 text-en-meta text-en-ink-muted">
              <span aria-hidden className="h-px flex-1 bg-en-line" />
              {t('login.orWithEmail')}
              <span aria-hidden className="h-px flex-1 bg-en-line" />
            </div>
          </div>
        )}

        {codeMode && codeActions ? (
          <LoginCodePanel
            request={codeActions.request}
            verify={codeActions.verify}
            next={nextUrl}
            initialEmail={initialEmail || pendingEmail || remembered || ''}
            onBack={() => setCodeMode(false)}
          />
        ) : (
          <form
            method="post"
            className="grid gap-5"
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit(new FormData(e.currentTarget))
            }}
          >
            {organization && <input type="hidden" name="orgSlug" value={organization.slug} />}
            {nextUrl && <input type="hidden" name="next" value={nextUrl} />}
            <Field
              label={t('fields.email')}
              id="email"
              name="email"
              type="email"
              // A pending sign-up's address prefills sign in only, never the
              // locked email of an invitation.
              key={`email-${remembered ?? ''}`}
              defaultValue={
                initialEmail || (mode === 'login' ? (pendingEmail ?? remembered ?? '') : '')
              }
              autoComplete="email"
              required
              placeholder={t('fields.emailPlaceholder')}
              inputMode="email"
              enterKeyHint="next"
              disabled={isLoading}
              readOnly={emailLocked}
            />
            {mode === 'login' && remembered && !initialEmail && !pendingEmail && (
              <p className="-mt-2 text-en-meta text-en-ink-muted" data-testid="remembered-email">
                {t('login.code.remembered.label', { email: remembered })}{' '}
                <button
                  type="button"
                  onClick={() => {
                    forgetRememberedEmail()
                    setRemembered(null)
                  }}
                  className={authLinkClass}
                >
                  {t('login.code.remembered.other')}
                </button>
              </p>
            )}
            <PasswordField
              key={passwordKey}
              label={mode === 'signup' ? t('login.createPassword') : t('fields.password')}
              help={mode === 'signup' ? t('fields.passwordHelp') : undefined}
              labelAction={
                mode === 'login' ? (
                  <Link href="/forgot-password" className={`text-en-meta ${authLinkClass}`}>
                    {t('login.forgotPassword')}
                  </Link>
                ) : undefined
              }
              id="password"
              name="password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              enterKeyHint="done"
              disabled={isLoading}
            />
            <div className="grid gap-3">
              <Button look="encuentro" variant="primary" type="submit" block disabled={isLoading}>
                {isLoading && <Loader2 aria-hidden className="animate-spin" />}
                {isLoading
                  ? mode === 'signup'
                    ? t('login.submittingSignup')
                    : t('login.submittingLogin')
                  : mode === 'signup'
                    ? t('login.submitSignup')
                    : t('login.submitLogin')}
              </Button>
              {mode === 'login' && codeActions && (
                <button
                  type="button"
                  onClick={() => setCodeMode(true)}
                  disabled={isLoading}
                  className={`text-center text-en-meta font-semibold ${authLinkClass}`}
                >
                  {t('login.code.entry')}
                </button>
              )}
              <ConsentLine />
            </div>
          </form>
        )}

        {(mode === 'signup' || organization) && (
          <p className="text-center text-en-meta text-en-ink-muted">
            {mode === 'login' ? t('login.noAccount') : t('login.haveAccount')}{' '}
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className={`font-semibold ${authLinkClass}`}
            >
              {mode === 'login' ? t('login.switchToSignup') : t('login.switchToLogin')}
            </button>
          </p>
        )}
      </div>
    </AuthShell>
  )
}
